import { useEffect, useState } from 'react';
import { getApexTiers } from '../lib/riot';

/**
 * APEX v7.5.0 — useApexTiers
 *
 * Récupère les cutoffs Master→GM, GM→Challenger et le LP du #1 Challenger
 * via l'Edge Function Supabase qui proxie l'API Riot.
 *
 * Fallback hardcodé si l'Edge Function n'est pas dispo (valeurs typiques EUW
 * estimées en mai 2026, à ajuster si nécessaire).
 *
 * Usage :
 *   const { tiers, loading } = useApexTiers('euw1');
 *   if (tiers) {
 *     // tiers.masterToGmCutoff
 *     // tiers.gmToChallengerCutoff
 *     // tiers.challengerTopLp
 *   }
 */

// v7.7.11 : valeurs fallback EUW réalistes. L'ancien masterToGmCutoff:400
// était la VRAIE source du "400" affiché (et non le fallback de RankProgressBar,
// qui n'était jamais atteint car ce hook retourne toujours cet objet, pas null).
// Ces valeurs restent approximatives — la vraie valeur temps réel viendra de
// l'Edge Function apex-tiers une fois déployée sur Supabase.
const FALLBACK_TIERS = {
  masterCount: 700,
  gmCount: 200,
  challengerCount: 50,
  masterToGmCutoff: 850,        // EUW ~850 LP (varie selon le ladder)
  gmToChallengerCutoff: 1100,   // EUW ~1100 LP (varie selon le ladder)
  challengerTopLp: 1800,        // EUW top #1 approximatif
  _fallback: true               // flag pour savoir si on est en fallback
};

export function useApexTiers(region = 'euw1') {
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getApexTiers(region);
        if (!mounted) return;
        if (data && !data.error) {
          setTiers(data);
        } else {
          // Edge function indispo → fallback
          setTiers(FALLBACK_TIERS);
        }
      } catch (e) {
        console.warn('[useApexTiers] Error, using fallback:', e);
        if (mounted) setTiers(FALLBACK_TIERS);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [region]);

  return { tiers, loading };
}

export default useApexTiers;
